import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { Subject } from 'rxjs';
import { UserService } from '../../../services/user.service';
import { AdminPermission } from '../../../enums/constants';

@Injectable({
  providedIn: 'root'
})
class AdminGuard {
  constructor(
    private userService: UserService,
    private router: Router
  ) { }

  canActivate() {
    const subject = new Subject<boolean | UrlTree>();
    this.userService.getAuthAdmin().subscribe(
      (response) => {
        this.userService.user = response.data;
        this.userService.usertype = response.data.usertype;
        subject.next(true);
      },
      (_err) => {
        this.userService.user = null;
        subject.next(this.router.parseUrl("/signin"));
      }
    );

    return subject.asObservable();
  }

  canActivateAdminRoute(route: ActivatedRouteSnapshot, requireAll: boolean) {
    const grantedPermissions = this.userService.user.granted_permissions ?? [];
    const requiredPermissions: AdminPermission[] = route.data.requiredPermissions ?? [];

    if (route.data.isAdminDashboardRoot) {
      const routeUrl = this.getFirstPageWithAdminPermission(grantedPermissions);
      if (routeUrl === '/admin/') {
        return true;
      } else {
        return this.router.parseUrl(routeUrl);
      }
    } else if (requireAll) {
      for (let requiredPermission of requiredPermissions) {
        if (!grantedPermissions.includes(requiredPermission)) {
          return this.router.parseUrl("/admin/access-denied");
        }
      }

      return true;
    } else {
      for (let requiredPermission of requiredPermissions) {
        if (grantedPermissions.includes(requiredPermission)) {
          return true;
        }
      }

      return this.router.parseUrl("/admin/access-denied");
    }
  }

  private getFirstPageWithAdminPermission(grantedPermissions) {
    const permissionAccordingToRoute = [
      { permission: AdminPermission.MENU_USERS_ALL, route: '' },
      { permission: AdminPermission.MENU_USERS_BLOCKED, route: 'blocked-users' }
    ];

    for (let { permission, route } of permissionAccordingToRoute) {
      if (grantedPermissions.includes(permission)) {
        return `/admin/${route}`;
      }
    }

    // all admin has access to this page.
    return '/admin/terms-and-conditions';
  }
}

export const canActivateAdmin: CanActivateFn = () => inject(AdminGuard).canActivate();

export const protectAdminRouteRequireAll: CanActivateFn = (route: ActivatedRouteSnapshot) => inject(AdminGuard).canActivateAdminRoute(route, true);

export const protectAdminRouteRequireAny: CanActivateFn = (route: ActivatedRouteSnapshot) => inject(AdminGuard).canActivateAdminRoute(route, false);
